/**
 * Picks the disclaimer a signed-in customer must be shown, by their market.
 *
 * ---------------------------------------------------------------------------------------------
 * The market comes from the CUSTOMER'S PROFILE, not from the deployment.
 * ---------------------------------------------------------------------------------------------
 * `appConfig.market` is a build-time constant baked into the bundle — it is the same value for
 * every visitor of a given deployment. Keying the disclaimer on it would show every customer of
 * the US build the US disclaimer, including India customers, which is exactly the bug the
 * watermark had: the ticker list was right (server-side, from the profile) while the branding was
 * wrong (client-side, from the build constant). Legal text is a worse thing to get wrong than a
 * watermark, so this resolves from `user.marketId` and treats appConfig only as a fallback for
 * anonymous or unknown cases.
 *
 * The India text is held verbatim as transcribed from the source PDF, with entity names inline
 * rather than as placeholders. The US text uses `{[ASTROSTOCKCHARTS.COM]}` / `{{COMPANY_NAME}}`
 * substitution; running that same substitution over text containing no placeholders is a no-op,
 * so both work through one render path. Verbatim was chosen deliberately: introducing
 * placeholders into legal wording risks a customer seeing a literal `{{COMPANY_NAME}}` if
 * substitution ever fails, and the safest legal text is the text as written.
 */

import { toMarketId } from './branding';
import indiaDisclaimer from '../config/disclaimer.india.json';

/** Market ids with their own disclaimer. Anything else falls back to the deployment config. */
export const DISCLAIMER_BY_MARKET_ID = {
  2: indiaDisclaimer,   // India — Dhanabhyas Astro Stock Research
};

/**
 * The disclaimer config for a market.
 *
 * @param {number|string|null} market      the customer's marketId (or market code)
 * @param {object|null}        defaultConfig  deployment appConfig, used for US / unknown markets
 * @returns {object} an object carrying `siteName`, `companyName` and `disclaimerText`
 */
export function disclaimerForMarket(market, defaultConfig) {
  const marketId = toMarketId(market);
  const specific = marketId === null ? null : DISCLAIMER_BY_MARKET_ID[marketId];
  if (!specific) return defaultConfig || null;

  // The market-specific document supplies its own names; anything it does not state falls back to
  // the deployment config so a partial override cannot leave a field undefined.
  return {
    ...defaultConfig,
    ...specific,
  };
}

/**
 * Whether the customer's market is known well enough to choose a disclaimer.
 *
 * This exists because of a race worth being explicit about. `login()` resolves as soon as Firebase
 * authenticates, but the profile carrying `marketId` is fetched separately and lands a moment
 * later. The disclaimer dialog opens in between. Rendering the default in that gap would flash the
 * US disclaimer at an India customer before swapping it — brief, but it is the wrong legal text in
 * front of the wrong customer, and it is the version they might actually read before clicking
 * Accept.
 *
 * A user object that arrived without a usable marketId (the degraded profile-fetch fallback) still
 * counts as resolved: waiting forever would be worse than showing the deployment default.
 */
export function isMarketResolved(user) {
  return !!user;
}

export default disclaimerForMarket;
